<template>
  <b-row
    v-if="loaded"
    align-h="center"
  >
    <b-col
      lg="12"
      xl="9"
      class="p-2"
    >
      <div class="item-text d-flex px-2 pb-2">
        <div class="d-flex align-items-center justify-content-center">
          <i
            :class="item.icon + ' mr-3 main'"
            aria-hidden="true"
          />
        </div>
        <div class="d-flex flex-column justify-content-start">
          <small>{{ $t('dashboard.widget.pendingCases') }}</small>
          <span>{{ $t(item.label) }}</span>
        </div>
      </div>
      <div class="d-flex mt-1">
        <div
          id="pop-priority-laboratories"
          class="form-icon d-flex mx-2 is-relative"
        >
          <i
            class="fal fa-hospital mt-2"
            aria-hidden="true"
          />
          <div class="bubble">
            <vtBubble
              :number="priorityChartLaboratoriesSelected.length"
              :info="true"
            />
          </div>
        </div>
        <b-popover
          v-if="priorityChartLaboratoriesSelected.length"
          target="pop-priority-laboratories"
          placement="right"
          triggers="hover"
        >
          <div class="px-3 py-2">
            <div class="mb-1">
              <span><strong>{{ $t('dashboard.widget.selectedCenters') }}</strong></span>
            </div>
            <div
              v-for="(lab, index) in priorityChartLaboratoriesSelected"
              :key="index"
            >
              {{ lab.labName }}
            </div>
          </div>
        </b-popover>
        <vt-combo-select
          v-model="priorityChartLaboratoriesSelected"
          :placeholder="$t('dashboard.widget.laboratory')"
          track-by="labId"
          :options="laboratories"
          label="labName"
          :multiple="true"
          :show-selection-number="true"
          :close-on-select="false"
          @input="refreshAndSaveConfiguration()"
        />
      </div>
      <div class="d-flex mt-1">
        <div
          id="pop-priority-case-types"
          class="form-icon d-flex mx-2 is-relative"
        >
          <i
            class="fal fa-chart-network mt-2"
            aria-hidden="true"
          />
          <div class="bubble">
            <vtBubble
              :number="priorityChartCaseTypesSelected.length"
              :info="true"
            />
          </div>
        </div>
        <b-popover
          v-if="priorityChartCaseTypesSelected.length"
          target="pop-priority-case-types"
          placement="right"
          triggers="hover"
        >
          <div class="px-3 py-2">
            <div class="mb-1">
              <span><strong>{{ $t('dashboard.widget.selectedCaseTypes') }}</strong></span>
            </div>
            <div
              v-for="(caseType, index) in priorityChartCaseTypesSelected"
              :key="index"
            >
              {{ caseType.description }}
            </div>
          </div>
        </b-popover>
        <vt-combo-select
          v-model="priorityChartCaseTypesSelected"
          :placeholder="$t('dashboard.widget.caseType')"
          track-by="id"
          :options="caseTypes"
          label="description"
          :multiple="true"
          :show-selection-number="true"
          :close-on-select="false"
          @input="refreshAndSaveConfiguration()"
        />
      </div>
      <div class="d-flex mt-1">
        <div class="form-icon d-flex mx-2 is-relative">
          <i
            class="fal fa-tags mt-2"
            aria-hidden="true"
          />
        </div>
        <vt-combo-select
          v-model="priorityChartSituationsSelected"
          class="mt-1"
          :placeholder="$t('dashboard.widget.caseSituation')"
          track-by="key"
          :options="caseSituations"
          :custom-label="v => [$t(v.label)].filter(Boolean).join(' ')"
          :multiple="false"
          @input="refreshAndSaveConfiguration($event)"
        />
      </div>
      <div class="mt-3">
        <priority-chart
          v-if="item.subType === 'casePriority' && caseTypes.length"
          ref="priorityChart"
          :laboratories-id="priorityChartLaboratoriesSelected.length ? priorityChartLaboratoriesSelected : laboratories"
          :casetypes-id="priorityChartCaseTypesSelected.length ? priorityChartCaseTypesSelected : caseTypes"
          :case-situation="priorityChartSituationsSelected ? priorityChartSituationsSelected : null"
        />
      </div>
    </b-col>
  </b-row>
</template>

<script>
import VtBubble from '@/components/common/VtBubble'
import PriorityChart from '@/modules/dashboardModule/widgets/chartWidget/PriorityChart'
import { DASHBOARD_SITUATION_CASE_OPTIONS } from '@/api/resources/dashboard/dashboardService'
import { StoreUtils } from '@/shared/util/store-utils'
import customConfigResource from '@/api/resources/ui/CustomConfigResource'

export default {
    name: 'ChartWidgetCasePriority',
    components: {
        VtBubble,
        PriorityChart
    },
    props: {
        item: { type: Object },
        caseTypes: { type: Array }
    },
    data() {
        return {
            priorityChartLaboratoriesSelected: [],
            priorityChartCaseTypesSelected: [],
            priorityChartSituationsSelected: null,
            uiComponent: 'uicomponent.dashboard.diagnosis.casepriority',
            loaded: false
        }
    },
    computed: {
        caseSituations() {
            return Object.keys(DASHBOARD_SITUATION_CASE_OPTIONS).filter(key => key !== DASHBOARD_SITUATION_CASE_OPTIONS.ALL_PENDING_DASHBOARD.key).map(key => DASHBOARD_SITUATION_CASE_OPTIONS[key])
        },
        laboratories() {
            return StoreUtils.laboratories
        }
    },
    async mounted() {
        const savedConfiguration = await customConfigResource.get(this.uiComponent)
        if (savedConfiguration && savedConfiguration.config) {
            this.priorityChartLaboratoriesSelected = savedConfiguration.config.priorityChartLaboratoriesSelected
            this.priorityChartCaseTypesSelected = savedConfiguration.config.priorityChartCaseTypesSelected
            this.priorityChartSituationsSelected = savedConfiguration.config.priorityChartSituationsSelected
        }
        this.loaded = true
    },
    methods: {
        refreshAndSaveConfiguration() {
            this.refreshPriorityChartSelected()
            this.saveConfiguration()
        },
        refreshPriorityChartSelected() {
            this.$nextTick(() => {
                this.$refs?.priorityChart?.fillData()
            })
        },
        saveConfiguration() {
            this.$nextTick(() => {
                const config = {
                    priorityChartLaboratoriesSelected: this.priorityChartLaboratoriesSelected,
                    priorityChartCaseTypesSelected: this.priorityChartCaseTypesSelected,
                    priorityChartSituationsSelected: this.priorityChartSituationsSelected
                }
                customConfigResource.save(this.uiComponent, config)
            })
        }
    }
}
</script>

<style scoped>

</style>
